import React, { useEffect, useState } from 'react';
import { CreditCardIcon, CalendarIcon, UserIcon, TagIcon, PlusIcon, XIcon, UploadIcon, TrashIcon, DollarSignIcon, SaveIcon, ChevronDownIcon, ChevronUpIcon, InfoIcon, RefreshCwIcon, AlertCircleIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
import { FileUpload } from '../FileUpload';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
export function ExpenseClaimForm({
  locale = 'en-US'
}) {
  const [expenseData, setExpenseData] = useState({
    title: '',
    date: '',
    description: '',
    defaultCurrency: 'USD',
    exchangeRateDate: new Date().toISOString().split('T')[0],
    department: '',
    project: '',
    totalAmount: 0,
    notes: '',
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: ''
  });
  const [expenseItems, setExpenseItems] = useState([{
    id: 1,
    description: '',
    date: new Date().toISOString().split('T')[0],
    category: '',
    amount: '',
    currency: 'USD',
    exchangeRate: 1,
    useSystemRate: true,
    amountInDefaultCurrency: '',
    manualConvertedAmount: '',
    conversionMode: 'system',
    taxCode: 'NONE',
    taxAmount: 0,
    hasReceipt: false,
    receipts: [],
    notes: ''
  }]);
  const [errors, setErrors] = useState({});
  const [showCurrencyConverter, setShowCurrencyConverter] = useState(false);
  const [exchangeRates, setExchangeRates] = useState({
    USD: 1,
    EUR: 0.85,
    GBP: 0.75,
    CAD: 1.25,
    AUD: 1.35,
    JPY: 110.5,
    CNY: 6.45,
    INR: 74.5
  });
  // Sample data for dropdowns
  const categories = ['Travel - Airfare', 'Travel - Hotel', 'Travel - Meals', 'Travel - Transportation', 'Office Supplies', 'Training & Conferences', 'Software & Subscriptions', 'Client Entertainment', 'Marketing & Advertising', 'Miscellaneous'];
  const currencies = [{
    code: 'USD',
    name: 'US Dollar'
  }, {
    code: 'EUR',
    name: 'Euro'
  }, {
    code: 'GBP',
    name: 'British Pound'
  }, {
    code: 'CAD',
    name: 'Canadian Dollar'
  }, {
    code: 'AUD',
    name: 'Australian Dollar'
  }, {
    code: 'JPY',
    name: 'Japanese Yen'
  }, {
    code: 'CNY',
    name: 'Chinese Yuan'
  }, {
    code: 'INR',
    name: 'Indian Rupee'
  }];
  const taxCodes = [{
    code: 'NONE',
    name: 'No Tax',
    rate: 0
  }, {
    code: 'GST10',
    name: 'GST 10%',
    rate: 0.1
  }, {
    code: 'VAT20',
    name: 'VAT 20%',
    rate: 0.2
  }, {
    code: 'HST13',
    name: 'HST 13%',
    rate: 0.13
  }, {
    code: 'EXEMPT',
    name: 'Tax Exempt',
    rate: 0
  }];
  const departments = ['Sales', 'Marketing', 'Finance', 'Human Resources', 'Operations', 'IT', 'Research & Development', 'Customer Support', 'Administration'];
  const projects = ['General Operations', 'Product Launch 2023', 'Client Project - ABC Corp', 'Client Project - XYZ Ltd', 'Marketing Campaign Q4', 'IT Infrastructure Upgrade', 'Annual Conference', 'Research Initiative'];
  // Simulate fetching the latest exchange rates based on date
  const fetchExchangeRates = date => {
    // In a real app, this would be an API call to get rates for the specific date
    console.log(`Fetching exchange rates for ${date}`);
    // Simulate slight variations in exchange rates based on date
    const dateParts = date.split('-');
    const day = parseInt(dateParts[2], 10);
    // Create slightly different rates based on the day of the month
    const variationFactor = 1 + day % 10 * 0.01;
    return {
      USD: 1,
      EUR: 0.85 * variationFactor,
      GBP: 0.75 * variationFactor,
      CAD: 1.25 * variationFactor,
      AUD: 1.35 * variationFactor,
      JPY: 110.5 * variationFactor,
      CNY: 6.45 * variationFactor,
      INR: 74.5 * variationFactor
    };
  };
  // Update exchange rates when the exchange rate date changes
  useEffect(() => {
    if (expenseData.exchangeRateDate) {
      const newRates = fetchExchangeRates(expenseData.exchangeRateDate);
      setExchangeRates(newRates);
      // Update all expense items that use system rates
      setExpenseItems(prevItems => prevItems.map(item => {
        if (item.conversionMode === 'system' && item.currency !== expenseData.defaultCurrency) {
          const systemRate = getExchangeRate(item.currency, expenseData.defaultCurrency, newRates);
          const amount = parseFloat(item.amount) || 0;
          return {
            ...item,
            exchangeRate: systemRate,
            amountInDefaultCurrency: (amount * systemRate).toFixed(2)
          };
        }
        return item;
      }));
    }
  }, [expenseData.exchangeRateDate]);
  // Helper function to get exchange rate between two currencies
  const getExchangeRate = (fromCurrency, toCurrency, rates = exchangeRates) => {
    if (fromCurrency === toCurrency) return 1;
    // If converting to USD, use the direct rate
    if (toCurrency === 'USD') {
      return 1 / rates[fromCurrency];
    }
    // If converting from USD, use the direct rate
    if (fromCurrency === 'USD') {
      return rates[toCurrency];
    }
    // Cross-currency conversion (through USD)
    const fromToUSD = 1 / rates[fromCurrency];
    const usdToTarget = rates[toCurrency];
    return fromToUSD * usdToTarget;
  };
  // Calculate total amount
  useEffect(() => {
    const total = expenseItems.reduce((sum, item) => {
      const itemAmount = parseFloat(item.amountInDefaultCurrency) || 0;
      return sum + itemAmount;
    }, 0);
    setExpenseData({
      ...expenseData,
      totalAmount: total
    });
  }, [expenseItems]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setExpenseData({
      ...expenseData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleItemChange = (id, field, value) => {
    setExpenseItems(expenseItems.map(item => {
      if (item.id === id) {
        const updatedItem = {
          ...item,
          [field]: value
        };
        // Update tax amount when tax code or amount changes
        if (field === 'taxCode' || field === 'amount') {
          const amount = parseFloat(updatedItem.amount) || 0;
          const taxRate = taxCodes.find(tax => tax.code === updatedItem.taxCode)?.rate || 0;
          updatedItem.taxAmount = amount * taxRate;
        }
        // Handle different conversion modes
        if (field === 'conversionMode') {
          updatedItem.useSystemRate = value === 'system';
          // Reset fields based on new mode
          if (value === 'system') {
            const systemRate = getExchangeRate(updatedItem.currency, expenseData.defaultCurrency);
            const amount = parseFloat(updatedItem.amount) || 0;
            updatedItem.exchangeRate = systemRate;
            updatedItem.amountInDefaultCurrency = (amount * systemRate).toFixed(2);
            updatedItem.manualConvertedAmount = '';
          } else if (value === 'rate') {
            // Keep existing exchange rate if switching from system
            const amount = parseFloat(updatedItem.amount) || 0;
            updatedItem.amountInDefaultCurrency = (amount * updatedItem.exchangeRate).toFixed(2);
            updatedItem.manualConvertedAmount = '';
          } else if (value === 'amount') {
            // Set manual converted amount to current calculated amount if available
            if (updatedItem.amountInDefaultCurrency) {
              updatedItem.manualConvertedAmount = updatedItem.amountInDefaultCurrency;
            }
          }
        }
        // Update amount in default currency when amount or currency changes
        if (field === 'amount' || field === 'currency') {
          const amount = parseFloat(updatedItem.amount) || 0;
          if (updatedItem.conversionMode === 'system') {
            const systemRate = getExchangeRate(updatedItem.currency, expenseData.defaultCurrency);
            updatedItem.exchangeRate = systemRate;
            updatedItem.amountInDefaultCurrency = (amount * systemRate).toFixed(2);
          } else if (updatedItem.conversionMode === 'rate') {
            updatedItem.amountInDefaultCurrency = (amount * updatedItem.exchangeRate).toFixed(2);
          } else if (updatedItem.conversionMode === 'amount') {
            // If in amount mode, recalculate exchange rate if we have both amounts
            const convertedAmount = parseFloat(updatedItem.manualConvertedAmount) || 0;
            if (amount > 0 && convertedAmount > 0) {
              updatedItem.exchangeRate = convertedAmount / amount;
              updatedItem.amountInDefaultCurrency = updatedItem.manualConvertedAmount;
            }
          }
        }
        // Handle manual converted amount changes
        if (field === 'manualConvertedAmount') {
          const amount = parseFloat(updatedItem.amount) || 0;
          const convertedAmount = parseFloat(value) || 0;
          if (amount > 0 && convertedAmount > 0) {
            // Calculate exchange rate based on the two amounts
            updatedItem.exchangeRate = convertedAmount / amount;
            updatedItem.amountInDefaultCurrency = value;
          } else {
            updatedItem.amountInDefaultCurrency = value;
          }
        }
        // If exchange rate changes directly
        if (field === 'exchangeRate') {
          const amount = parseFloat(updatedItem.amount) || 0;
          updatedItem.amountInDefaultCurrency = (amount * value).toFixed(2);
        }
        // If currency changes, update exchange rate based on system rates if using automatic conversion
        if (field === 'currency' && updatedItem.conversionMode === 'system') {
          const newRate = getExchangeRate(value, expenseData.defaultCurrency);
          updatedItem.exchangeRate = newRate;
          const amount = parseFloat(updatedItem.amount) || 0;
          updatedItem.amountInDefaultCurrency = (amount * newRate).toFixed(2);
        }
        return updatedItem;
      }
      return item;
    }));
  };
  const addExpenseItem = () => {
    const newId = expenseItems.length > 0 ? Math.max(...expenseItems.map(item => item.id)) + 1 : 1;
    setExpenseItems([...expenseItems, {
      id: newId,
      description: '',
      date: new Date().toISOString().split('T')[0],
      category: '',
      amount: '',
      currency: expenseData.defaultCurrency,
      exchangeRate: 1,
      useSystemRate: true,
      conversionMode: 'system',
      amountInDefaultCurrency: '',
      manualConvertedAmount: '',
      taxCode: 'NONE',
      taxAmount: 0,
      hasReceipt: false,
      receipts: [],
      notes: ''
    }]);
  };
  const removeExpenseItem = id => {
    if (expenseItems.length > 1) {
      setExpenseItems(expenseItems.filter(item => item.id !== id));
    }
  };
  const handleReceiptToggle = id => {
    setExpenseItems(expenseItems.map(item => {
      if (item.id === id) {
        return {
          ...item,
          hasReceipt: !item.hasReceipt
        };
      }
      return item;
    }));
  };
  const handleFileUpload = (id, newFiles) => {
    setExpenseItems(expenseItems.map(item => {
      if (item.id === id) {
        return {
          ...item,
          receipts: [...item.receipts, ...newFiles]
        };
      }
      return item;
    }));
  };
  const removeFile = (itemId, fileIndex) => {
    setExpenseItems(expenseItems.map(item => {
      if (item.id === itemId) {
        const updatedReceipts = [...item.receipts];
        updatedReceipts.splice(fileIndex, 1);
        return {
          ...item,
          receipts: updatedReceipts
        };
      }
      return item;
    }));
  };
  const updateExchangeRate = (id, value) => {
    handleItemChange(id, 'exchangeRate', value);
  };
  const refreshSystemRate = id => {
    const item = expenseItems.find(item => item.id === id);
    if (item) {
      const systemRate = getExchangeRate(item.currency, expenseData.defaultCurrency);
      handleItemChange(id, 'exchangeRate', systemRate);
    }
  };
  const validateForm = () => {
    const newErrors = {};
    // Validate main form fields
    if (!expenseData.title) newErrors.title = 'Title is required';
    if (!expenseData.date) newErrors.date = 'Date is required';
    // Validate expense items
    const itemErrors = [];
    expenseItems.forEach((item, index) => {
      const itemError = {};
      if (!item.description) itemError.description = 'Description is required';
      if (!item.category) itemError.category = 'Category is required';
      if (!item.amount || isNaN(parseFloat(item.amount)) || parseFloat(item.amount) <= 0) {
        itemError.amount = 'Valid amount is required';
      }
      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });
    if (itemErrors.length > 0) {
      newErrors.items = itemErrors;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      // Prepare data for submission
      const formattedData = {
        ...expenseData,
        items: expenseItems.map(item => ({
          ...item,
          amount: parseFloat(item.amount) || 0,
          amountInDefaultCurrency: parseFloat(item.amountInDefaultCurrency) || 0,
          taxAmount: parseFloat(item.taxAmount) || 0
        }))
      };
      console.log('Submitting expense claim:', formattedData);
      alert('Expense claim submitted successfully!');
      // Navigate back to dashboard
      if (window.setCurrentView) {
        window.setCurrentView('expense-management');
      }
    }
  };
  const handleSaveAsDraft = () => {
    // Save as draft without validation
    console.log('Saving as draft:', {
      ...expenseData,
      items: expenseItems
    });
    alert('Expense claim saved as draft!');
    // Navigate back to dashboard
    if (window.setCurrentView) {
      window.setCurrentView('expense-management');
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-7264">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-7265">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-7266">
          Create New Expense Claim
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-7267">
          Please fill in all required fields to submit your expense claim.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="p-6" data-id="element-7268">
        {/* Main expense claim information */}
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-7269">
          <div className="sm:col-span-4" data-id="element-7270">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700" data-id="element-7271">
              Expense Title <span className="text-red-500" data-id="element-7272">*</span>
            </label>
            <div className="mt-1" data-id="element-7273">
              <input type="text" name="title" id="title" value={expenseData.title} onChange={handleChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.title ? 'border-red-300' : ''}`} placeholder="e.g., Client Meeting Expenses" data-id="element-7274" />
              {errors.title && <p className="mt-1 text-sm text-red-600" data-id="element-7275">{errors.title}</p>}
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-7276">
            <label htmlFor="date" className="block text-sm font-medium text-gray-700" data-id="element-7277">
              Date <span className="text-red-500" data-id="element-7278">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm" data-id="element-7279">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7280">
                <CalendarIcon className="h-5 w-5 text-gray-400" data-id="element-7281" />
              </div>
              <input type="date" name="date" id="date" value={expenseData.date} onChange={handleChange} className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md ${errors.date ? 'border-red-300' : ''}`} data-id="element-7282" />
              {errors.date && <p className="mt-1 text-sm text-red-600" data-id="element-7283">{errors.date}</p>}
            </div>
          </div>
          <div className="sm:col-span-3" data-id="element-7284">
            <label htmlFor="department" className="block text-sm font-medium text-gray-700" data-id="element-7285">
              Department
            </label>
            <div className="mt-1" data-id="element-7286">
              <select id="department" name="department" value={expenseData.department} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7287">
                <option value="" data-id="element-7288">Select Department</option>
                {departments.map(dept => <option key={dept} value={dept} data-id="element-7289">
                    {dept}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="sm:col-span-3" data-id="element-7290">
            <label htmlFor="project" className="block text-sm font-medium text-gray-700" data-id="element-7291">
              Project
            </label>
            <div className="mt-1" data-id="element-7292">
              <select id="project" name="project" value={expenseData.project} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7293">
                <option value="" data-id="element-7294">Select Project</option>
                {projects.map(proj => <option key={proj} value={proj} data-id="element-7295">
                    {proj}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="sm:col-span-3" data-id="element-7296">
            <label htmlFor="defaultCurrency" className="block text-sm font-medium text-gray-700" data-id="element-7297">
              Default Currency
            </label>
            <div className="mt-1" data-id="element-7298">
              <select id="defaultCurrency" name="defaultCurrency" value={expenseData.defaultCurrency} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7299">
                {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-7300">
                    {currency.code} - {currency.name}
                  </option>)}
              </select>
            </div>
          </div>
          <div className="sm:col-span-3" data-id="element-7301">
            <label htmlFor="exchangeRateDate" className="block text-sm font-medium text-gray-700" data-id="element-7302">
              Exchange Rate Date
            </label>
            <div className="mt-1" data-id="element-7303">
              <input type="date" name="exchangeRateDate" id="exchangeRateDate" value={expenseData.exchangeRateDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7304" />
              <p className="mt-1 text-xs text-gray-500" data-id="element-7305">
                System exchange rates will be based on this date
              </p>
            </div>
          </div>
          <div className="sm:col-span-6" data-id="element-7306">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-7307">
              Description
            </label>
            <div className="mt-1" data-id="element-7308">
              <textarea id="description" name="description" rows={3} value={expenseData.description} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Provide details about this expense claim..." data-id="element-7309" />
            </div>
          </div>
        </div>
        {/* Expense Items */}
        <div className="mt-8" data-id="element-7310">
          <div className="flex justify-between items-center mb-4" data-id="element-7311">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7312">Expense Items</h3>
            <button type="button" onClick={addExpenseItem} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7313">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-7314" />
              Add Item
            </button>
          </div>
          {expenseItems.map((item, index) => <div key={item.id} className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-7315">
              <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-7316">
                <h4 className="text-base font-medium text-gray-900" data-id="element-7317">
                  Item {index + 1}
                </h4>
                <button type="button" onClick={() => removeExpenseItem(item.id)} className="text-red-600 hover:text-red-900" disabled={expenseItems.length === 1} data-id="element-7318">
                  <TrashIcon className="h-5 w-5" data-id="element-7319" />
                </button>
              </div>
              <div className="p-4" data-id="element-7320">
                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-7321">
                  {/* Description */}
                  <div className="sm:col-span-6" data-id="element-7322">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7323">
                      Description <span className="text-red-500" data-id="element-7324">*</span>
                    </label>
                    <div className="mt-1" data-id="element-7325">
                      <input type="text" value={item.description} onChange={e => handleItemChange(item.id, 'description', e.target.value)} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.items && errors.items[index]?.description ? 'border-red-300' : ''}`} placeholder="Describe the expense item" data-id="element-7326" />
                      {errors.items && errors.items[index]?.description && <p className="mt-1 text-sm text-red-600" data-id="element-7327">
                          {errors.items[index].description}
                        </p>}
                    </div>
                  </div>
                  {/* Date */}
                  <div className="sm:col-span-2" data-id="element-7328">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7329">
                      Date
                    </label>
                    <div className="mt-1" data-id="element-7330">
                      <input type="date" value={item.date} onChange={e => handleItemChange(item.id, 'date', e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7331" />
                    </div>
                  </div>
                  {/* Category */}
                  <div className="sm:col-span-4" data-id="element-7332">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7333">
                      Category <span className="text-red-500" data-id="element-7334">*</span>
                    </label>
                    <div className="mt-1" data-id="element-7335">
                      <select value={item.category} onChange={e => handleItemChange(item.id, 'category', e.target.value)} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.items && errors.items[index]?.category ? 'border-red-300' : ''}`} data-id="element-7336">
                        <option value="" data-id="element-7337">Select a category</option>
                        {categories.map(category => <option key={category} value={category} data-id="element-7338">
                            {category}
                          </option>)}
                      </select>
                      {errors.items && errors.items[index]?.category && <p className="mt-1 text-sm text-red-600" data-id="element-7339">
                          {errors.items[index].category}
                        </p>}
                    </div>
                  </div>
                  {/* Amount and Currency */}
                  <div className="sm:col-span-2" data-id="element-7340">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7341">
                      Amount <span className="text-red-500" data-id="element-7342">*</span>
                    </label>
                    <div className="mt-1" data-id="element-7343">
                      <input type="number" value={item.amount} onChange={e => handleItemChange(item.id, 'amount', e.target.value)} min="0" step="0.01" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.items && errors.items[index]?.amount ? 'border-red-300' : ''}`} placeholder="0.00" data-id="element-7344" />
                      {errors.items && errors.items[index]?.amount && <p className="mt-1 text-sm text-red-600" data-id="element-7345">
                          {errors.items[index].amount}
                        </p>}
                    </div>
                  </div>
                  <div className="sm:col-span-2" data-id="element-7346">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7347">
                      Currency
                    </label>
                    <div className="mt-1" data-id="element-7348">
                      <select value={item.currency} onChange={e => handleItemChange(item.id, 'currency', e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7349">
                        {currencies.map(currency => <option key={currency.code} value={currency.code} data-id="element-7350">
                            {currency.code}
                          </option>)}
                      </select>
                    </div>
                  </div>
                  {/* Tax Code and Amount */}
                  <div className="sm:col-span-2" data-id="element-7351">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7352">
                      Tax Code
                    </label>
                    <div className="mt-1" data-id="element-7353">
                      <select value={item.taxCode} onChange={e => handleItemChange(item.id, 'taxCode', e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-7354">
                        {taxCodes.map(tax => <option key={tax.code} value={tax.code} data-id="element-7355">
                            {tax.name} ({tax.rate * 100}%)
                          </option>)}
                      </select>
                    </div>
                  </div>
                  <div className="sm:col-span-2" data-id="element-7356">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7357">
                      Tax Amount
                    </label>
                    <div className="mt-1" data-id="element-7358">
                      <div className="mt-1 relative rounded-md shadow-sm" data-id="element-7359">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7360">
                          <span className="text-gray-500 sm:text-sm" data-id="element-7361">
                            {item.currency}
                          </span>
                        </div>
                        <input type="text" value={item.taxAmount.toFixed(2)} readOnly className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-12 pr-12 sm:text-sm border-gray-300 bg-gray-50 rounded-md" data-id="element-7362" />
                      </div>
                    </div>
                  </div>
                  {/* Currency Conversion */}
                  {item.currency !== expenseData.defaultCurrency && <div className="sm:col-span-6 mt-2" data-id="element-7363">
                      <div className="flex items-center justify-between" data-id="element-7364">
                        <div className="flex items-center" data-id="element-7365">
                          <InfoIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-7366" />
                          <span className="text-sm text-gray-700" data-id="element-7367">
                            Currency conversion needed
                          </span>
                        </div>
                        <button type="button" onClick={() => setShowCurrencyConverter(!showCurrencyConverter)} className="text-sm text-indigo-600 hover:text-indigo-800" data-id="element-7368">
                          {showCurrencyConverter ? 'Hide Converter' : 'Show Converter'}
                        </button>
                      </div>
                      {showCurrencyConverter && <div className="mt-2 p-4 bg-gray-50 rounded-md border border-gray-200" data-id="element-7369">
                          <div className="mb-3" data-id="element-7370">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-7371">
                              Conversion Method
                            </label>
                            <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4" data-id="element-7372">
                              <div className="flex items-center" data-id="element-7373">
                                <input id={`conversionMode-system-${item.id}`} type="radio" checked={item.conversionMode === 'system'} onChange={() => handleItemChange(item.id, 'conversionMode', 'system')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7374" />
                                <label htmlFor={`conversionMode-system-${item.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-7375">
                                  Use system rate
                                </label>
                              </div>
                              <div className="flex items-center" data-id="element-7376">
                                <input id={`conversionMode-rate-${item.id}`} type="radio" checked={item.conversionMode === 'rate'} onChange={() => handleItemChange(item.id, 'conversionMode', 'rate')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7377" />
                                <label htmlFor={`conversionMode-rate-${item.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-7378">
                                  Enter exchange rate
                                </label>
                              </div>
                              <div className="flex items-center" data-id="element-7379">
                                <input id={`conversionMode-amount-${item.id}`} type="radio" checked={item.conversionMode === 'amount'} onChange={() => handleItemChange(item.id, 'conversionMode', 'amount')} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300" data-id="element-7380" />
                                <label htmlFor={`conversionMode-amount-${item.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-7381">
                                  Enter converted amount
                                </label>
                              </div>
                            </div>
                          </div>
                          {item.conversionMode === 'system' && <div className="flex items-center justify-between mb-3" data-id="element-7382">
                              <div className="text-sm text-gray-700" data-id="element-7383">
                                Using system exchange rate as of{' '}
                                {expenseData.exchangeRateDate}
                              </div>
                              <button type="button" onClick={() => refreshSystemRate(item.id)} className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7384">
                                <RefreshCwIcon className="h-3 w-3 mr-1" data-id="element-7385" />
                                Refresh
                              </button>
                            </div>}
                          {item.conversionMode === 'rate' && <div className="flex items-center justify-between" data-id="element-7386">
                              <div className="text-sm text-gray-700" data-id="element-7387">
                                <span className="font-medium" data-id="element-7388">
                                  Exchange Rate:
                                </span>{' '}
                                1 {item.currency} =
                              </div>
                              <div className="flex items-center" data-id="element-7389">
                                <input type="number" value={item.exchangeRate} onChange={e => updateExchangeRate(item.id, parseFloat(e.target.value))} min="0.01" step="0.01" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 w-24 sm:text-sm border-gray-300 rounded-md mr-2" data-id="element-7390" />
                                <span className="text-sm text-gray-700" data-id="element-7391">
                                  {expenseData.defaultCurrency}
                                </span>
                              </div>
                            </div>}
                          {item.conversionMode === 'amount' && <div className="flex items-center justify-between" data-id="element-7392">
                              <div className="text-sm text-gray-700" data-id="element-7393">
                                <span className="font-medium" data-id="element-7394">
                                  Amount in {expenseData.defaultCurrency}:
                                </span>
                              </div>
                              <div className="flex items-center" data-id="element-7395">
                                <div className="relative rounded-md shadow-sm" data-id="element-7396">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7397">
                                    <span className="text-gray-500 sm:text-sm" data-id="element-7398">
                                      {expenseData.defaultCurrency}
                                    </span>
                                  </div>
                                  <input type="number" value={item.manualConvertedAmount} onChange={e => handleItemChange(item.id, 'manualConvertedAmount', e.target.value)} min="0" step="0.01" className="focus:ring-indigo-500 focus:border-indigo-500 block w-32 pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" data-id="element-7399" />
                                </div>
                              </div>
                            </div>}
                          <div className="mt-3 flex justify-between items-center" data-id="element-7400">
                            <div className="text-sm text-gray-700" data-id="element-7401">
                              Amount in {expenseData.defaultCurrency}:
                            </div>
                            <div className="font-medium" data-id="element-7402">
                              {formatCurrency(parseFloat(item.amountInDefaultCurrency) || 0, expenseData.defaultCurrency, locale)}
                            </div>
                          </div>
                          {item.amount && item.amountInDefaultCurrency && <div className="mt-2 text-xs text-gray-500" data-id="element-7403">
                              Effective exchange rate: 1 {item.currency} ={' '}
                              {(parseFloat(item.amountInDefaultCurrency) / parseFloat(item.amount)).toFixed(4)}{' '}
                              {expenseData.defaultCurrency}
                            </div>}
                        </div>}
                    </div>}
                  {/* Receipt Toggle */}
                  <div className="sm:col-span-6 mt-2" data-id="element-7404">
                    <div className="flex items-center" data-id="element-7405">
                      <input id={`hasReceipt-${item.id}`} type="checkbox" checked={item.hasReceipt} onChange={() => handleReceiptToggle(item.id)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-7406" />
                      <label htmlFor={`hasReceipt-${item.id}`} className="ml-2 block text-sm text-gray-700" data-id="element-7407">
                        I have a receipt for this item
                      </label>
                    </div>
                  </div>
                  {/* Receipt Upload */}
                  {item.hasReceipt && <div className="sm:col-span-6 mt-2" data-id="element-7408">
                      <div className="rounded-md border border-gray-200 p-4" data-id="element-7409">
                        <FileUpload onFileUpload={files => handleFileUpload(item.id, files)} data-id="element-7410" />
                        {item.receipts.length > 0 && <div className="mt-3" data-id="element-7411">
                            <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-7412">
                              Uploaded Receipts
                            </h5>
                            <ul className="divide-y divide-gray-200 border-t border-b border-gray-200" data-id="element-7413">
                              {item.receipts.map((file, fileIndex) => <li key={fileIndex} className="py-2 flex justify-between items-center" data-id="element-7414">
                                  <div className="flex items-center" data-id="element-7415">
                                    <UploadIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-7416" />
                                    <span className="text-sm text-gray-600" data-id="element-7417">
                                      {file.name}
                                    </span>
                                  </div>
                                  <button type="button" onClick={() => removeFile(item.id, fileIndex)} className="text-red-600 hover:text-red-900" data-id="element-7418">
                                    <XIcon className="h-4 w-4" data-id="element-7419" />
                                  </button>
                                </li>)}
                            </ul>
                          </div>}
                      </div>
                    </div>}
                  {/* Notes */}
                  <div className="sm:col-span-6 mt-2" data-id="element-7420">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-7421">
                      Notes
                    </label>
                    <div className="mt-1" data-id="element-7422">
                      <textarea value={item.notes} onChange={e => handleItemChange(item.id, 'notes', e.target.value)} rows={2} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Additional information about this expense..." data-id="element-7423" />
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
          {/* Total Summary */}
          <div className="mt-6 flex justify-end" data-id="element-7424">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-300 w-64" data-id="element-7425">
              <div className="flex justify-between items-center text-lg font-medium" data-id="element-7426">
                <span data-id="element-7427">Total Amount:</span>
                <span data-id="element-7428">
                  {formatCurrency(expenseData.totalAmount, expenseData.defaultCurrency, locale)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Notes Section */}
        <div className="mt-8" data-id="element-7429">
          <StandardizedNotes value={expenseData.notes} onChange={value => setExpenseData({
          ...expenseData,
          notes: value
        })} visibility={expenseData.noteVisibility} onVisibilityChange={visibility => setExpenseData({
          ...expenseData,
          noteVisibility: visibility
        })} visibilityDetails={expenseData.noteVisibilityDetails} onVisibilityDetailsChange={details => setExpenseData({
          ...expenseData,
          noteVisibilityDetails: details
        })} label="Expense Claim Notes" placeholder="Add any additional notes or comments about this expense claim. Select who can view these notes using the visibility options." data-id="element-7430" />
        </div>
        {/* Form Actions */}
        <div className="pt-8 flex justify-end space-x-3" data-id="element-7431">
          <button type="button" onClick={handleSaveAsDraft} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7432">
            <SaveIcon className="h-4 w-4 mr-1 inline-block" data-id="element-7433" />
            Save as Draft
          </button>
          <button type="submit" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-7434">
            Submit Expense
          </button>
        </div>
      </form>
    </div>;
}